import { MonthlyExpenseGroup } from "../../types/monthly-expense.type";
import { roundHalfUp } from "../../utils/money";

/**
 * The money of the month close (handbook "ปิดเดือนและประมาณการ"), a port of the server's
 * `MonthlyExpenseEntity` so a figure previewed here is the one the server stores: every sum and
 * share rounds half-up to the satang; CEIL is used only on the M+1 estimate.
 */

/** ยอดเดือน (editable) · รายวัน (÷ วันขาย) · รายสาย (÷ วันขาย ÷ สาย). */
export type AmountView = "MONTH" | "DAY" | "ROUTE";

export interface GroupedAmount {
  group: MonthlyExpenseGroup;
  amount: number;
}

/** A branch's month: the S4 group columns and the two figures the estimate is made of. */
export interface MonthTotals {
  dailySales: number;
  dailyAdmin: number;
  /** The 16 items of the group plus its extras. */
  sales: number;
  salary: number;
  admin: number;
  /** Sales = every SALES amount. */
  salesTotal: number;
  /** Manager = every SALARY amount + every ADMIN amount. */
  managerTotal: number;
  total: number;
}

export const sumMoney = (amounts: number[]): number =>
  roundHalfUp(amounts.reduce((sum, amount) => sum + amount, 0));

const sumOf = (amounts: GroupedAmount[], group: MonthlyExpenseGroup) =>
  sumMoney(amounts.filter((item) => item.group === group).map((item) => item.amount));

/** The server's `totalsOf`: `monthly` is the 16 items followed by the extras. */
export const totalsOf = (daily: GroupedAmount[], monthly: GroupedAmount[]): MonthTotals => {
  const dailySales = sumOf(daily, "SALES");
  const dailyAdmin = sumOf(daily, "ADMIN");
  const sales = sumOf(monthly, "SALES");
  const salary = sumOf(monthly, "SALARY");
  const admin = sumOf(monthly, "ADMIN");
  const salesTotal = roundHalfUp(dailySales + sales);
  const managerTotal = roundHalfUp(salary + dailyAdmin + admin);
  return {
    dailySales,
    dailyAdmin,
    sales,
    salary,
    admin,
    salesTotal,
    managerTotal,
    total: roundHalfUp(salesTotal + managerTotal),
  };
};

/** amount ÷ วันขาย ÷ สาย, half-up; null when the branch ran no route in the month. */
export const perRouteDay = (amount: number, saleDays: number, routeCount: number): number | null =>
  saleDays <= 0 || routeCount <= 0 ? null : roundHalfUp(amount / saleDays / routeCount);

/** One Sales or Manager cell: the system value, the value in effect for M+1, and the gap. */
export interface EstimateFigures {
  /** = M's actual (report "จริง"); adjustments never change it. */
  computed: number | null;
  /** The M+1 estimate: head office's adjustment, else computed. */
  effective: number | null;
  isAdjusted: boolean;
  /** effective − computed; 0 unless adjusted. */
  difference: number;
  /** difference × วันขาย × สาย: what the adjustment adds to the branch's month. */
  monthlyImpact: number;
}

export const estimateFiguresOf = (
  computed: number | null,
  adjusted: number | null,
  routeDays: number,
): EstimateFigures => {
  const isAdjusted = adjusted !== null && (computed === null || roundHalfUp(adjusted) !== computed);
  const difference =
    isAdjusted && adjusted !== null && computed !== null ? roundHalfUp(adjusted - computed) : 0;
  return {
    computed,
    effective: adjusted ?? computed,
    isAdjusted,
    difference,
    monthlyImpact: roundHalfUp(difference * routeDays),
  };
};

/** ปัดขึ้นเป็นจำนวนเต็ม: always up (CEIL) from the 2-dp figure, never to the nearest. */
export const roundUpEstimate = (value: number): number => Math.ceil(roundHalfUp(value));

/** A branch's figure as the chosen view shows it; null when there is nothing to divide by. */
export const viewAmountOf = (
  amount: number,
  view: AmountView,
  saleDays: number,
  routeCount: number,
): number | null => {
  if (view === "MONTH") return amount;
  if (view === "DAY") return saleDays > 0 ? roundHalfUp(amount / saleDays) : null;
  return perRouteDay(amount, saleDays, routeCount);
};

export interface BranchAmount {
  amount: number;
  saleDays: number;
  routeCount: number;
}

/**
 * A column on the รวม row: the month's sum; รายวัน = the branches' daily figures added up;
 * รายสาย = the sum over every route-day of the company (a weighted average).
 */
export const viewTotalOf = (entries: BranchAmount[], view: AmountView): number | null => {
  if (view === "MONTH") return sumMoney(entries.map((entry) => entry.amount));
  if (view === "DAY") {
    const ran = entries.filter((entry) => entry.saleDays > 0);
    return ran.length ? sumMoney(ran.map((entry) => entry.amount / entry.saleDays)) : null;
  }
  const ran = entries.filter((entry) => entry.saleDays > 0 && entry.routeCount > 0);
  const routeDays = ran.reduce((sum, entry) => sum + entry.saleDays * entry.routeCount, 0);
  if (routeDays === 0) return null;
  return roundHalfUp(ran.reduce((sum, entry) => sum + entry.amount, 0) / routeDays);
};

export interface WeightedValue {
  value: number | null;
  routeDays: number;
}

/** Σ value × route-days ÷ Σ route-days over the branches that have a value. */
export const weightedAverageOf = (entries: WeightedValue[]): number | null => {
  const counted = entries.filter((entry) => entry.value !== null && entry.routeDays > 0);
  const routeDays = counted.reduce((sum, entry) => sum + entry.routeDays, 0);
  if (routeDays === 0) return null;
  const weighted = counted.reduce((sum, entry) => sum + (entry.value ?? 0) * entry.routeDays, 0);
  return roundHalfUp(weighted / routeDays);
};
